import React, { useEffect, useState } from "react";
import { AppCSS } from "../../../../components";
import {
  AddClaimInput,
  CustomerProjectDetails,
} from "../../../../redux/types/projectDetails";
import { useMutation } from "@apollo/client";
import { ADD_CLAIM_DETAILS } from "../../../../apolloClient/operation/mutations";
import { toast } from "react-toastify";
import { logEvent, PROJECT_EVENTS } from "../../../../utils/amplitude";
import { Box } from "@mui/material";

interface MaterialProps {
  project: CustomerProjectDetails;
  refetchData: Function;
  permissions?: boolean;
}

export default function InsuranceInfo({
  project,
  refetchData,
  permissions,
}: MaterialProps) {
  const [insurer, setInsurer] = useState("");
  const [claimNumber, setClaimNumber] = useState("");
  const [claimDate, setClaimDate] = useState("");
  const [totalPayout, setTotalPayout] = useState(null);
  const [firstCheck, setFirstCheck] = useState(null);
  const [secondCheck, setSecondCheck] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [originalDetails, setOriginalDetails] = useState({
    insurer: "",
    claimNumber: "",
    claimDate: "",
    totalPayout: null,
    firstCheck: null,
    secondCheck: null,
  });

  const [addClaimDetails] = useMutation<
    {
      addClaimDetails: boolean;
    },
    { input: AddClaimInput }
  >(ADD_CLAIM_DETAILS);

  useEffect(() => {
    if (project) {
      setInsurer(project?.insuranceScope.insuranceClaim.Insurer);
      setClaimNumber(project?.insuranceScope.insuranceClaim.ClaimNumber);
      setClaimDate(project?.insuranceScope.insuranceClaim.ClaimDate);
      setTotalPayout(project?.insuranceScope.insuranceClaim.TotalPayout);
      setFirstCheck(project?.insuranceScope.insuranceClaim.FirstCheck);
      setSecondCheck(project?.insuranceScope.insuranceClaim.SecondCheck);
      setOriginalDetails({
        insurer: project?.insuranceScope.insuranceClaim.Insurer,
        claimNumber: project?.insuranceScope.insuranceClaim.ClaimNumber,
        claimDate: project?.insuranceScope.insuranceClaim.ClaimDate,
        totalPayout: project?.insuranceScope.insuranceClaim.TotalPayout,
        firstCheck: project?.insuranceScope.insuranceClaim.FirstCheck,
        secondCheck: project?.insuranceScope.insuranceClaim.SecondCheck,
      });
    }
  }, [project]);

  const validateDate = (date: string) => {
    // mm-dd-yyyy format
    const regex = /^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])-\d{4}$/;
    return regex.test(date);
  };

  const saveNewItem = async () => {
    if (!validateDate(claimDate)) {
      toast("Invalid date format (mm-dd-yyyy)", { type: "error" });
      return;
    }

    const changedDetails = {
      insurer,
      claimNumber,
      claimDate,
      totalPayout,
      firstCheck,
      secondCheck,
    };

    if (JSON.stringify(changedDetails) === JSON.stringify(originalDetails)) {
      toast("No changes detected", {
        type: "info",
        position: "top-left",
        className: "toast-message",
      });
      return;
    }

    try {
      await addClaimDetails({
        variables: {
          input: {
            projectId: project.projectId,
            insurer: insurer || "",
            claimNumber: claimNumber || "",
            claimDate: claimDate || "",
            totalPayout: totalPayout || 0,
            firstCheck: firstCheck || 0,
            secondCheck: secondCheck || 0,
          },
        },
      });
      toast("Insurance details saved successfully", { type: "success" });
      
      // Track insurance details update
      const fieldsUpdated = [];
      if (insurer) fieldsUpdated.push('insurer');
      if (claimNumber) fieldsUpdated.push('claimNumber');
      if (claimDate) fieldsUpdated.push('claimDate');
      if (totalPayout) fieldsUpdated.push('totalPayout');
      logEvent(PROJECT_EVENTS.INSURANCE_UPDATED, { 
        project_id: project.projectId, 
        fields_updated: fieldsUpdated 
      });
      
      refetchData();
    } catch (error) {
      toast("Error saving insurance details. Please try again.", {
        type: "error",
      });
    }
  };

  const handleEditClick = async () => {
    setIsEditing(!isEditing);
    if (isEditing) {
      await saveNewItem();
    }
  };

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "flex-end", marginBottom: 3 }}>
        {permissions && (
          <Box
            component="button"
            onClick={handleEditClick}
            sx={{
              padding: "10px 24px",
              backgroundColor: "#4b87ed",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontSize: 14,
              fontWeight: 500,
              cursor: "pointer",
              transition: "background-color 0.2s",
              "&:hover": {
                backgroundColor: "#3a6fd8",
              },
            }}
          >
            {isEditing ? "Save" : "Edit"}
          </Box>
        )}
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
          gap: 3,
        }}
      >
        <Box>
          <Box
            sx={{
              fontSize: 13,
              fontWeight: 500,
              color: "#374151",
              mb: 1,
            }}
          >
            Insurer
          </Box>
          <input
            value={insurer}
            className="placeholder-black"
            readOnly={!isEditing}
            onChange={(e) => setInsurer(e.target.value)}
            style={{
              width: "100%",
              height: 40,
              padding: "0 12px",
              border: "1px solid #e5e7eb",
              borderRadius: 6,
              fontSize: 14,
              outline: "none",
              backgroundColor: isEditing ? "white" : "#f9fafb",
            }}
          />
        </Box>
        <Box>
          <Box
            sx={{
              fontSize: 13,
              fontWeight: 500,
              color: "#374151",
              mb: 1,
            }}
          >
            Claim number
          </Box>
          <input
            value={claimNumber}
            className="placeholder-black"
            readOnly={!isEditing}
            onChange={(e) => setClaimNumber(e.target.value)}
            style={{
              width: "100%",
              height: 40,
              padding: "0 12px",
              border: "1px solid #e5e7eb",
              borderRadius: 6,
              fontSize: 14,
              outline: "none",
              backgroundColor: isEditing ? "white" : "#f9fafb",
            }}
          />
        </Box>
        <Box>
          <Box
            sx={{
              fontSize: 13,
              fontWeight: 500,
              color: "#374151",
              mb: 1,
            }}
          >
            Insurance scope date (mm-dd-yyyy)
          </Box>
          <input
            value={claimDate}
            className="placeholder-black"
            readOnly={!isEditing}
            onChange={(e) => setClaimDate(e.target.value)}
            style={{
              width: "100%",
              height: 40,
              padding: "0 12px",
              border: "1px solid #e5e7eb",
              borderRadius: 6,
              fontSize: 14,
              outline: "none",
              backgroundColor: isEditing ? "white" : "#f9fafb",
            }}
          />
        </Box>

        <Box>
          <Box
            sx={{
              fontSize: 13,
              fontWeight: 500,
              color: "#374151",
              mb: 1,
            }}
          >
            First check
          </Box>
          <input
            value={firstCheck}
            type="number"
            className="placeholder-black"
            readOnly={!isEditing}
            onChange={(e) => setFirstCheck(Number(e.target.value))}
            style={{
              width: "100%",
              height: 40,
              padding: "0 12px",
              border: "1px solid #e5e7eb",
              borderRadius: 6,
              fontSize: 14,
              outline: "none",
              backgroundColor: isEditing ? "white" : "#f9fafb",
            }}
          />
        </Box>
        <Box>
          <Box
            sx={{
              fontSize: 13,
              fontWeight: 500,
              color: "#374151",
              mb: 1,
            }}
          >
            Second check
          </Box>
          <input
            value={secondCheck}
            className="placeholder-black"
            type="number"
            readOnly={!isEditing}
            onChange={(e) => setSecondCheck(Number(e.target.value))}
            style={{
              width: "100%",
              height: 40,
              padding: "0 12px",
              border: "1px solid #e5e7eb",
              borderRadius: 6,
              fontSize: 14,
              outline: "none",
              backgroundColor: isEditing ? "white" : "#f9fafb",
            }}
          />
        </Box>
        <Box>
          <Box
            sx={{
              fontSize: 13,
              fontWeight: 500,
              color: "#374151",
              mb: 1,
            }}
          >
            Total payout
          </Box>
          <input
            value={totalPayout}
            className="placeholder-black"
            type="number"
            readOnly={!isEditing}
            onChange={(e) => setTotalPayout(Number(e.target.value))}
            style={{
              width: "100%",
              height: 40,
              padding: "0 12px",
              border: "1px solid #e5e7eb",
              borderRadius: 6,
              fontSize: 14,
              outline: "none",
              backgroundColor: isEditing ? "white" : "#f9fafb",
            }}
          />
        </Box>
      </Box>
    </>
  );
}
